import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import {
  Destroyable,
  SaveForm,
  isSomething,
  selectCurrentAccount,
  untilDestroy,
  Account,
} from '@tokeet-frontend/tv3-platform'
import { Store, select } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { MessagingChannelType, WhatsappMessageTemplate, MessagingChannelsService } from '@tokeet-frontend/message'
import { combineLatest, of } from 'rxjs'
import { map, catchError, finalize } from 'rxjs/operators'
import * as lodash from 'lodash'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { DataDictService } from '@tv3/services/data-dict.service'
import { MatRadioChange } from '@angular/material/radio'
import { AddMessage } from '@tv3/store/message/message.actions'

@Component({
  selector: 'app-send-whatsapp-template-dialog',
  templateUrl: './send-whatsapp-template-dialog.component.html',
  host: { class: 'modal-content' },
})
export class SendWhatsappTemplateDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    whatsappTemplateId: ['', [Validators.required]],
    whatsappTemplateVariables: ['', [Validators.required]],
    messageHtml: ['', [Validators.required]],
    messageText: ['', [Validators.required]],
    channel: [MessagingChannelType.Whatsapp],
  })

  get inquiry() {
    return this.data.inquiry
  }

  get compiledTemplate() {
    return this.form.getRawValue().messageText
  }

  isLoading = true
  account: Account
  whatsappTemplates: WhatsappMessageTemplate[] = []

  constructor(
    private dialogRef: MatDialogRef<SendWhatsappTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: { inquiry: Inquiry },
    private store: Store<fromRoot.State>,
    private fb: FormBuilder,
    private dataDictService: DataDictService,
    private messagingChannelsService: MessagingChannelsService
  ) {
    super()
  }

  ngOnInit(): void {
    this.store.pipe(select(selectCurrentAccount), untilDestroy(this)).subscribe((account) => {
      this.account = account
    })
    this.messagingChannelsService
      .getWhatsappTemplates()
      .pipe(
        catchError(() => of([])),
        finalize(() => (this.isLoading = false)),
        untilDestroy(this)
      )
      .subscribe((templates) => {
        this.whatsappTemplates = templates.filter((t) => t.templateType === 'booking')
      })
  }

  onClose() {
    this.dialogRef.close()
  }

  onTemplateChange(data: MatRadioChange) {
    const template = this.whatsappTemplates.find((t) => t.sid === data.value)
    if (!template) return
    this.setWhatsappTemplate(template)
  }

  compileDataTokens(content: string) {
    // Approved WhatsApp template variables must be plain text without newlines (Twilio error 92007).
    return this.dataDictService
      .resolve(
        content,
        {
          inquiry: this.inquiry,
          rental: this.inquiry.rental,
          guest: this.inquiry.guest,
          account: this.account,
        },
        {},
        { plainText: true }
      )
      .pipe(map((text) => text.replace(/\s*\n\s*/g, '; ')))
  }

  compileWhatsappTemplate(template: WhatsappMessageTemplate) {
    if (!isSomething(template.variables)) return of({ content: template.content, variables: {} })

    const content = template.content

    return combineLatest(
      lodash.map(template.variables || [], (tag) => {
        return this.compileDataTokens(tag)
      })
    ).pipe(
      // Render the template with the variables
      map((variables) => ({
        content: content.replace(/{{(\d+)}}/g, (_, key) => variables[key - 1]),
        variables: lodash.mapKeys(
          lodash.mapValues(variables, (t) => t),
          (v, i) => +i + 1
        ),
      }))
    )
  }

  setWhatsappTemplate(template: WhatsappMessageTemplate) {
    this.compileWhatsappTemplate(template).subscribe(({ content, variables }) => {
      this.form.patchValue({
        whatsappTemplateVariables: JSON.stringify(variables),
        // content is plain text, so we need to convert it to html
        messageHtml: content.replace(/\n/g, '<br>'),
        messageText: content,
      })
    })
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { channel, ...data } = form.getRawValue()
    this.store.dispatch(
      AddMessage({
        inquiry: this.inquiry,
        form: { ...data, whatsapp_phone: this.inquiry?.guest?.whatsapp_phone },
        channel,
      })
    )
    this.dialogRef.close()
  }
}
